import type { ComponentType } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import {
  LegacyCrmDetailRedirect,
  LegacyDossierWizardRedirect,
  LegacyRedirect,
  LegacyVaultSourcesRedirect,
} from "./legacy-redirects";

import type { Features } from "@/lib/features";
import { FeatureRoute } from "@/routes/feature-route";
import { settingsRoutes } from "@/routes/settings-routes";
import { importWithStaleChunkReload } from "@/lib/stale-chunk";

function lazyPage<K extends PropertyKey, T extends Record<K, ComponentType>>(
  load: () => Promise<T>,
  exportName: K,
) {
  return async () => {
    const module = await importWithStaleChunkReload(load);
    return { Component: module[exportName] };
  };
}

/** Same as `lazyPage`, but the surface flag must be on to render the page. */
function lazyFeaturePage<K extends PropertyKey, T extends Record<K, ComponentType>>(
  feature: keyof Features,
  load: () => Promise<T>,
  exportName: K,
) {
  return async () => {
    const module = await importWithStaleChunkReload(load);
    const Page: ComponentType = module[exportName];
    return {
      Component: () => (
        <FeatureRoute feature={feature}>
          <Page />
        </FeatureRoute>
      ),
    };
  };
}

// Exporté séparément pour pouvoir monter les mêmes routes dans un
// `createMemoryRouter` en test (cf. `router.test.tsx`).
export const routes = [
  {
    path: "/login",
    lazy: lazyPage(() => import("@/pages/login"), "LoginPage"),
  },
  {
    path: "/signup",
    lazy: lazyPage(() => import("@/pages/signup"), "SignupPage"),
  },
  {
    path: "/forgot-password",
    lazy: lazyPage(() => import("@/pages/forgot-password"), "ForgotPasswordPage"),
  },
  {
    path: "/reset-password",
    lazy: lazyPage(() => import("@/pages/reset-password"), "ResetPasswordPage"),
  },
  {
    // Confirmation d'adresse au signup self-serve (#549). Publique : le lien
    // peut être ouvert depuis un appareil sans session.
    path: "/verifier-email",
    lazy: lazyPage(() => import("@/pages/verify-email"), "VerifyEmailPage"),
  },
  {
    path: "/plateforme",
    lazy: lazyPage(() => import("@/pages/platform-console"), "PlatformConsolePage"),
    children: [
      {
        index: true,
        lazy: lazyPage(() => import("@/pages/platform-cabinets"), "PlatformCabinetsPage"),
      },
      {
        path: "cabinets/:organizationId",
        lazy: lazyPage(
          () => import("@/pages/platform-cabinet-detail"),
          "PlatformCabinetDetailPage",
        ),
      },
      {
        path: "operateurs",
        lazy: lazyPage(() => import("@/pages/platform-operators"), "PlatformOperatorsPage"),
      },
    ],
  },
  {
    // Page publique : accès sans login, juste avec le token URL.
    path: "/recueil/:token",
    lazy: lazyPage(() => import("@/pages/public/public-recueil"), "PublicRecueilPage"),
  },
  {
    // Page publique : lien mobile éphémère généré depuis l'app CGP.
    path: "/capture/:token",
    lazy: lazyPage(() => import("@/pages/public/mobile-capture"), "PublicMobileCapturePage"),
  },
  {
    path: "/",
    lazy: lazyPage(() => import("./protected-layout"), "ProtectedLayout"),
    children: [
      // Page Clients (Simplicité S8) : l'entrée de l'application, à la place
      // de l'ancien accueil (ses tuiles KPI iront à une page admin, lot S9).
      { index: true, lazy: lazyPage(() => import("@/pages/clients"), "ClientsPage") },
      { path: "clients", lazy: lazyPage(() => import("@/pages/clients"), "ClientsPage") },
      {
        path: "dossiers",
        lazy: lazyPage(() => import("@/pages/client-files-list"), "ClientFilesListPage"),
      },
      // Le wizard de création en 3 étapes est retiré (lot S4) : la création
      // flash est la seule entrée. L'ancienne URL reste pour les favoris.
      { path: "dossiers/nouveau", element: <LegacyRedirect to="/dossiers/flash" /> },
      {
        path: "dossiers/flash",
        lazy: lazyPage(() => import("@/pages/client-file-flash"), "ClientFileFlashPage"),
      },
      {
        path: "dossiers/:id",
        lazy: lazyPage(() => import("@/pages/client-file-detail"), "ClientFileDetailPage"),
      },
      // Le « mode guidé » d'un dossier est retiré (lot Simplicité S5) : les
      // anciens liens retombent sur la fiche dossier.
      { path: "dossiers/:id/wizard", element: <LegacyDossierWizardRedirect /> },
      {
        path: "crm",
        lazy: lazyFeaturePage("crm", () => import("@/pages/persons-list"), "PersonsListPage"),
      },
      {
        path: "crm/nouveau",
        lazy: lazyFeaturePage("crm", () => import("@/pages/person-new"), "PersonNewPage"),
      },
      {
        // Fiche client (Simplicité S3) — URL canonique. Volontairement SANS
        // garde `crm` : la fiche est le point d'édition de l'identité depuis
        // un dossier (« Modifier dans le CRM », foyer, signatures) et doit
        // rester joignable même quand la surface CRM (liste + création) est
        // masquée par le flag.
        path: "clients/:id",
        lazy: lazyPage(() => import("@/pages/person-detail"), "PersonDetailPage"),
      },
      // Ancienne URL de la fiche : redirigée (paramètres `?tab=` conservés).
      { path: "crm/:id", element: <LegacyCrmDetailRedirect /> },
      {
        path: "coffre",
        lazy: lazyPage(() => import("@/pages/documents"), "DocumentsPage"),
      },
      { path: "coffre/fichiers", element: <LegacyVaultSourcesRedirect /> },
      // Compatibilité des favoris, liens d'emails et anciennes URLs.
      { path: "personnes", element: <LegacyRedirect to="/crm" /> },
      { path: "personnes/nouveau", element: <LegacyRedirect to="/crm/nouveau" /> },
      { path: "personnes/:id", element: <LegacyCrmDetailRedirect /> },
      { path: "documents", element: <LegacyRedirect to="/coffre" /> },
      { path: "fichiers", element: <LegacyVaultSourcesRedirect /> },
      { path: "notes", lazy: lazyPage(() => import("@/pages/notes"), "NotesPage") },
      { path: "taches", lazy: lazyPage(() => import("@/pages/work-queue"), "WorkQueuePage") },
      {
        path: "pilotage",
        lazy: lazyPage(() => import("@/pages/pilotage"), "PilotagePage"),
      },
      {
        path: "signatures",
        lazy: lazyPage(() => import("@/pages/signatures"), "SignaturesPage"),
      },
      {
        path: "reglementaire",
        lazy: lazyPage(() => import("@/pages/regulatory-cockpit"), "RegulatoryCockpitPage"),
      },
      {
        path: "capture-mobile",
        lazy: lazyPage(() => import("@/pages/mobile-capture-cockpit"), "MobileCaptureCockpitPage"),
      },
      {
        // Une sous-page par section (lot Simplicité S10) : cf. settings-routes.
        path: "reglages",
        lazy: lazyPage(() => import("@/pages/settings"), "SettingsPage"),
        children: settingsRoutes,
      },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
];

export const router = createBrowserRouter(routes);
